import { useState, useEffect, useRef } from 'react'

export function useCountUp(
  target: number,
  duration: number = 2000,
  startOnView: boolean = true,
) {
  const [count, setCount] = useState(0)
  const elementRef = useRef<HTMLDivElement>(null)
  const hasStarted = useRef(false)

  useEffect(() => {
    const element = elementRef.current
    if (!element && startOnView) return

    const startAnimation = () => {
      if (hasStarted.current) return
      hasStarted.current = true

      const startTime = performance.now()
      const startValue = 0

      const animate = (currentTime: number) => {
        const elapsedTime = currentTime - startTime
        const progress = Math.min(elapsedTime / duration, 1)

        // Ease out quartic
        const easeOut = 1 - Math.pow(1 - progress, 4)

        const currentCount = Math.floor(
          startValue + (target - startValue) * easeOut,
        )
        setCount(currentCount)

        if (progress < 1) {
          requestAnimationFrame(animate)
        } else {
          setCount(target)
        }
      }

      requestAnimationFrame(animate)
    }

    if (startOnView) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            startAnimation()
            observer.disconnect()
          }
        },
        { threshold: 0.1 },
      )

      if (element) {
        observer.observe(element)
      }

      return () => observer.disconnect()
    } else {
      startAnimation()
    }
  }, [target, duration, startOnView])

  return { count, ref: elementRef }
}
